/**
 * MIRROR of `packages/shared/src/types/calibration-api.ts`.
 *
 * Why a mirror and not an import: `packages/web/Dockerfile` copies ONLY
 * `packages/web` into the build image and runs a bare `npm install` there, so a
 * workspace dependency on `@recruiter-buddy/shared` cannot resolve and would
 * break the production web image. Same arrangement as
 * `packages/web/src/constants/calibration-chips.ts`.
 *
 * Drift guard: `packages/shared/src/types/__tests__/calibration-api-mirror.test.ts`
 * asserts the text below this header matches the shared file byte-for-byte.
 * If you edit one, edit both — that test fails otherwise.
 *
 * This file is the reason the judging page no longer needs
 * `data as unknown as SomeLocallyInventedShape`: the page and the route now
 * describe the same contract, so a server change that drops a field the page
 * reads is a compile error instead of a blank screen in production.
 */

/* ==== MIRRORED CONTRACT BELOW — keep byte-identical with packages/web/src/types/calibration-api.ts ==== */

/**
 * Cosine-similarity stratum the calibration sampler drew this row from.
 *
 * US-500: still exported because the sampler and the POST-verdict `/reveal`
 * surface both use the vocabulary. It is deliberately NOT a field on
 * `CalibrationCandidateRow` — see the blindness note there.
 */
export type CalibrationScoreBand = 'top' | 'upper_mid' | 'lower_mid' | 'floor';

/** Recruiter's blind verdict. Null until judged. */
export type CalibrationVerdict = 'match' | 'not_match' | 'unsure';

/**
 * One calibration row as the judging UI sees it.
 *
 * `redactedResumeText` — NOT `resumeText`. Judging is blind on both axes:
 * score-blind (aiReviewScore/aiReviewId are never selected) and identity-blind.
 * The model reads `candidates.sanitized_resume_text`; the recruiter must read
 * the same artifact or the agreement rate the reveal screen reports is
 * measuring information asymmetry rather than taste. The raw resume never goes
 * over this wire — identity is a separate, recorded route.
 *
 * US-500 — THE THIRD BLIND AXIS: similarity. `cosineScore` and `scoreBand`
 * used to be here, and the judging card rendered the band as "top band" /
 * "floor band" in its header, ABOVE the verdict buttons, on every unjudged
 * candidate. The excuse recorded in the client hook was that both fields
 * existed "for later round-end summary use" — but the round-end summary reads
 * `/reveal`, a different route, so nothing consumed them for that purpose and
 * the leak was live. They are gone from this type, from the judging GET's
 * SELECT, and from the judge PATCH's RETURNING.
 *
 * The absence is the enforcement. A field on the wire is a field some future
 * component will render, and a reviewer noticing is not a mechanism — the same
 * argument this type already makes by having no `candidateName`. Anything
 * similarity-derived (a rank, a position, a match count, a percentage) belongs
 * to the same class: a count is a machine verdict wearing a plain shirt, and
 * the divergence the round-end reveal reports is only meaningful if the
 * recruiter's verdict was formed without any of it.
 */
export interface CalibrationCandidateRow {
  id: number;
  candidateId: number;
  recruiterVerdict: CalibrationVerdict | null;
  reasonChip: string | null;
  redactedResumeText: string | null;
  /**
   * US-492. The recruiter's own note, so stepping back to a judged candidate
   * shows what they actually wrote rather than an empty box. Carries no
   * AI-derived or similarity-derived signal, so it is not a blindness
   * regression — `aiReviewScore`/`aiReviewId` remain unselected by this route
   * and a test above pins that.
   */
  recruiterNote: string | null;
  /**
   * US-492. ISO timestamp of the FIRST judgment on this row; null until judged.
   * Set once — an edit does not move it, because (a) the undo stack is ordered
   * by it and a re-dated row would reorder under the recruiter's feet, and
   * (b) `judged_at` vs `identity_revealed_at` is the evidence that a verdict
   * was recorded blind, which a re-date would falsify.
   *
   * Null rather than absent: the client derives "has this been judged?" from
   * the value, and an optional key would make `'judgedAt' in row` load-bearing.
   */
  judgedAt: string | null;
  /**
   * US-503. Seconds the recruiter spent in the deliberate reading view for
   * this candidate before judging, accumulated across multiple opens.
   *
   * Null — never absent — means no reading view was ever opened, which is the
   * state of every row judged on the fast path and of every row that predates
   * the column. Deliberately distinct from `0`, which would mean "opened and
   * closed instantly"; the client renders a "researched" marker on the former
   * absence and nothing on the latter, so collapsing them would put a false
   * provenance claim on screen.
   *
   * Sent back on the wire, and sent UP on the judge PATCH as an optional body
   * field, because the marker has to survive a reload and a step back — this
   * page polls every ten seconds and re-renders judged rows from server data,
   * so a client-only timer is not a record of anything.
   *
   * This is the recruiter's own behavioural data and carries nothing AI- or
   * similarity-derived, so it is not a blindness regression — the same
   * argument this contract already makes for `recruiterNote`.
   */
  researchSeconds: number | null;
}

/**
 * Job context for the page header (walkthrough item 1 — "no job context at
 * all"). Deliberately does NOT carry the agent's current search-spec terms:
 * those are the model's live hypothesis, and showing them mid-round turns
 * "does this person fit the job?" into "do you agree with the machine?".
 */
export interface CalibrationJobContext {
  id: number;
  title: string;
  location: string | null;
  /**
   * Capped at `CALIBRATION_REQUIREMENTS_MAX_CHARS`. See
   * `requirementsTruncated` — do NOT infer abridgement from the length, and do
   * not present this as the whole job spec.
   */
  requirements: string | null;
  /**
   * US-501. True when `requirements` above is only the first
   * `CALIBRATION_REQUIREMENTS_MAX_CHARS` characters of the real job spec.
   *
   * REQUIRED and always present, never optional — an optional key would make
   * `'requirementsTruncated' in job` load-bearing on the client, the same
   * implicit-contract shape this file already rejects for `failureReason`.
   *
   * It exists because the truncation was previously SILENT: a 2,000-character
   * string ending in `…` and a genuinely 2,000-character JD are identical on
   * the wire. That was tolerable for a collapsed <details> summary and is not
   * tolerable for a reading view that compares a candidate against the job —
   * there the recruiter judges against a spec that is *wrong* rather than
   * merely short, and learns to trust the abridged version.
   *
   * Computed server-side from the PRE-truncation value. False when
   * `requirements` is null: an absent spec is not an abridged one.
   *
   * The honest full text lives behind `CalibrationJobSpecResponse` — fetched
   * lazily, only by surfaces that actually need it. This flag is what makes
   * the polled card honest on its own, and is the fallback when that fetch
   * fails.
   */
  requirementsTruncated: boolean;
  /**
   * Deliberately NOT capped and deliberately carries no truncation flag. The
   * asymmetry with `requirements` is intentional: adding a cap here to settle
   * a consistency itch would introduce a second lie in order to explain the
   * first.
   */
  screeningCriteriaText: string | null;
}

/** Server-side truncation limit for `requirements`, so a 40KB JD can't bloat every poll. */
export const CALIBRATION_REQUIREMENTS_MAX_CHARS = 2000;

/**
 * US-503. Upper bound on `researchSeconds`, in seconds. One hour.
 *
 * Clamped rather than rejected: a reading view left open overnight is a real
 * thing that happens, and it is not a reason to refuse a verdict the recruiter
 * legitimately made. 3600 then means "at least an hour", which is honest
 * enough for a signal used as a coarse flag. Tab-visibility pausing is
 * deliberately not implemented — precision theatre on a coarse flag.
 */
export const CALIBRATION_RESEARCH_SECONDS_MAX = 3600;

/**
 * The calibration round status vocabulary.
 *
 * 'failed' (product decision 2, 2026-08-04) is terminal: every candidate in
 * the round failed its AI review, so nothing landed and there is nothing to
 * judge. The worker used to DELETE such a round; it must not, because a
 * recruiter's URL (/sourcing-agents/:id/calibration/:roundId) can already
 * point at it and a round that silently vanishes is the same "failure looks
 * like nothing happened" shape that hid five months of zero passive-sourcing
 * leads.
 */
export type CalibrationRoundStatus = 'pending' | 'in_progress' | 'complete' | 'failed';

export const CALIBRATION_ROUND_STATUS_FAILED = 'failed';

/**
 * Narrowing helper rather than a `status` field typed as the union above.
 *
 * `sourcing_agent_calibration_rounds.status` is a plain varchar, so what the
 * route reads out of Postgres genuinely IS `string` — declaring it as the
 * union on the wire would require a cast somewhere, and a cast is how this
 * epic previously hid a whole contract mismatch. Consumers ask this question
 * instead.
 */
export function isFailedCalibrationRound(status: string): boolean {
  return status === CALIBRATION_ROUND_STATUS_FAILED;
}

/**
 * Round progress, so the page can render an honest denominator (walkthrough
 * item 7 / "Candidate 1 of 1").
 *
 * `batchSize` is what the round INTENDS to hold; `candidateCount` is how many
 * rows have actually landed. Calibration rows are inserted one at a time as
 * each AI review finishes, so for the first few minutes of a round these
 * differ — and the page must say "still screening" rather than confidently
 * print a denominator that is about to change.
 *
 * `failureReason` is REQUIRED and null on a healthy round, not optional. An
 * optional key would make `'failureReason' in round` a load-bearing test on
 * the client — an implicit contract of exactly the kind that let this page and
 * its route disagree for a whole wave.
 */
export interface CalibrationRoundInfo {
  id: number;
  roundNumber: number;
  status: string;
  batchSize: number;
  candidateCount: number;
  /** Why the round failed, in recruiter-readable words. Null unless status is 'failed'. */
  failureReason: string | null;
}

/**
 * GET /api/sourcing-agents/:agentId/calibration-rounds/:roundId/job-spec
 *
 * US-502. The job's spec, whole — **neither field is capped**. This is the
 * honest half of the truncation fix; `CalibrationJobContext.requirements` on
 * the polled `/candidates` payload stays capped, and says so.
 *
 * A SEPARATE ROUTE ON PURPOSE. `/candidates` is re-fetched every ten seconds
 * while a round fills, so putting a 40KB JD on it charges ~2.4MB of re-sent
 * constant data to the nine candidates in twelve that are judged in fifteen
 * seconds and never read closely. This payload is per-round constant, so a
 * reading surface fetches it once, lazily, on first open.
 *
 * It carries **no candidate, similarity, score or search-spec data of any
 * kind**, and its test asserts an exact key set rather than a denylist. It is
 * rendered beside blind judging controls; a field arriving here is a blindness
 * leak through a door nobody is watching. `mustHaveTerms`/`exclusionTerms` are
 * excluded for the same reason `CalibrationJobContext` excludes them: they are
 * the model's live hypothesis, and showing them mid-round turns "does this
 * person fit the job?" into "do you agree with the machine?".
 *
 * Nulls stay null and are never coerced to `''` — a reading view must be able
 * to distinguish "this job records no requirements" from "the requirements
 * failed to load", and an empty string renders identically to both.
 */
export interface CalibrationJobSpecResponse {
  id: number;
  title: string;
  location: string | null;
  /** The complete text. No cap, no ellipsis. */
  requirements: string | null;
  /** The complete text. No cap, no ellipsis. */
  screeningCriteriaText: string | null;
}

/** GET /api/sourcing-agents/:agentId/calibration-rounds/:roundId/candidates */
export interface CalibrationCandidatesResponse {
  candidates: CalibrationCandidateRow[];
  job: CalibrationJobContext;
  round: CalibrationRoundInfo;
}

/**
 * GET /api/sourcing-agents/:agentId/calibration-candidates/:rowId/identity
 *
 * The deliberate de-anonymising click. Fetching this records
 * `sourcing_agent_calibration_candidates.identity_revealed_at` — without that
 * record "blind by default" is unverifiable after the fact and the reveal
 * screen cannot separate blind judgments from sighted ones.
 */
export interface CalibrationIdentityResponse {
  candidateId: number;
  candidateName: string;
  email: string | null;
  phone: string | null;
  srCandidateId: string | null;
  /** Absolute SmartRecruiters person-page URL, or null when srCandidateId is missing. */
  srProfileUrl: string | null;
  /** ISO timestamp of the FIRST reveal for this row — re-reveals do not move it. */
  revealedAt: string;
}

/** One term-level change a refinement proposal wants to make to the search spec. */
export interface RefinementDiffEntry {
  field: string;
  action: 'add' | 'remove';
  term: string;
}

/**
 * A refinement proposal as the diff card sees it.
 *
 * `status` is load-bearing, not decoration: the GET route falls back to the
 * most recently RESOLVED proposal for the round when none is pending, so every
 * consumer must branch on it before offering Accept/Reject. A resolved proposal
 * rendered with live buttons is what made a successful accept look dead in
 * production on 2026-08-02.
 */
export interface RefinementProposalRow {
  id: number;
  status: string;
  proposedDiff: RefinementDiffEntry[];
  reasoning: string;
}

/**
 * GET  /api/sourcing-agents/:agentId/calibration-rounds/:roundId/refinement-proposal
 * POST /api/sourcing-agents/:agentId/refinement-proposal/:proposalId/accept
 *
 * The GET is ROUND-scoped (Critical C-A, 2026-08-04) and the accept is not,
 * because they identify different things: a proposal is looked up by the round
 * that produced it, and resolved by its own id. The agent-scoped GET this
 * replaced answered "did the round I just judged change anything?" with the
 * newest proposal the AGENT had ever had — in production, one accepted days
 * earlier for a different set of candidates — and the card rendered it as
 * "Applied — here's what changed". A 404 from the round-scoped GET means
 * exactly one thing: this round produced no proposal.
 *
 * `proposedDiff` is what the model asked for; `appliedDiff` is what the server
 * will actually write, after the bare-token exclusion rule
 * (`exclusion-term-validation.ts`) has had its say. They differ whenever the
 * model proposes an exclusion that isn't role-shaped.
 *
 * Both are on the wire because rendering `proposedDiff` under "Applied —
 * here's what changed" is a lie: the recruiter sees a term the server silently
 * refused to apply, and every later decision is made against a search they
 * believe they configured. `droppedDiffEntries` existed on the accept response
 * already and was rendered by nothing — so it also had to move onto the GET,
 * or the truth would survive exactly one render and be lost on refetch.
 *
 * The split is derived, never stored: the validator is deterministic over
 * `proposedDiff`, so any read of the same proposal reports the same thing.
 */
export interface RefinementProposalResponse {
  proposal: RefinementProposalRow;
  /** The subset of `proposal.proposedDiff` the server will apply / did apply. */
  appliedDiff: RefinementDiffEntry[];
  /** Entries the server refused to apply. Always present, possibly empty. */
  droppedDiffEntries: RefinementDiffEntry[];
  /** Plain-language reason the dropped entries were dropped; null when none were. */
  droppedReason: string | null;
}

/* ==========================================================================
 * RUNTIME CONTRACT KEYS (US-459)
 *
 * The types above are erased at runtime, which is the whole problem. A route
 * test that does `JSON.parse(res.payload) as CalibrationCandidatesResponse`
 * has asserted NOTHING — a cast is a claim, not a check — and every calibration
 * route test in this repo did exactly that. So a route could stop sending a
 * field, or send it under a different name, and the suite would stay green
 * until a recruiter saw a blank card.
 *
 * These key tuples are the bridge. `contractKeys<T>()` refuses to compile
 * unless the tuple lists EXACTLY the keys of T — no more, no fewer — so the
 * tuple cannot drift from the interface. Tests then compare a real HTTP body's
 * key set against the tuple, which is a genuine runtime check.
 *
 * The chain that makes a silent divergence impossible:
 *   1. rename a field in the interface -> the key tuple no longer compiles
 *   2. update the tuple               -> route tests fail until the route sends
 *                                        the new name
 *   3. this file changed              -> the byte-identical web mirror fails
 *                                        its drift test until web is updated
 * Every step is mechanical. None of them is "someone remembers to check".
 * ========================================================================== */

type ExactContractKeys<T, K extends readonly (keyof T)[]> =
  [Exclude<keyof T, K[number]>] extends [never]
    ? K
    : K & { readonly MISSING_FROM_CONTRACT_KEYS: Exclude<keyof T, K[number]> };

/**
 * `contractKeys<SomeWireType>()([...])` — a runtime key list the compiler
 * proves is exactly that type's keys.
 *
 * Omit a key and the call fails with "Property 'MISSING_FROM_CONTRACT_KEYS' is
 * missing"; add one that isn't on the type and it fails with "not assignable to
 * type 'keyof T'".
 */
export function contractKeys<T>() {
  return <K extends readonly (keyof T)[]>(keys: ExactContractKeys<T, K>): readonly (keyof T)[] =>
    keys as readonly (keyof T)[];
}

/**
 * Compare a value's own enumerable keys against a contract.
 *
 * Returns both directions because they are different bugs: `missing` means the
 * server stopped sending something a client's type promises (blank UI);
 * `unexpected` means the server sends something no contract describes (the
 * shape drifted and nobody wrote it down — and on these routes it may be PII
 * that was never meant to cross the wire).
 *
 * `undefined` counts as MISSING, not present: `JSON.stringify` drops undefined
 * properties entirely, so a field the server sets to `undefined` never reaches
 * the client no matter what the type says.
 */
export function contractKeyDiff(
  value: unknown,
  keys: readonly (string | number | symbol)[]
): { missing: string[]; unexpected: string[] } {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return { missing: keys.map(String), unexpected: [] };
  }
  const record = value as Record<string, unknown>;
  const expected = new Set(keys.map(String));
  const actual = Object.keys(record).filter((k) => record[k] !== undefined);

  return {
    missing: [...expected].filter((k) => !actual.includes(k)).sort(),
    unexpected: actual.filter((k) => !expected.has(k)).sort(),
  };
}

export const CALIBRATION_CANDIDATE_ROW_KEYS = contractKeys<CalibrationCandidateRow>()([
  'id',
  'candidateId',
  // US-500 removed `cosineScore` and `scoreBand` from this row: they are the
  // third blind axis, and a value on the wire is a value some future component
  // renders. They are deliberately absent here, not forgotten — the contract
  // gate proved the removal reached the wire by failing when they were still
  // listed. `/reveal` still carries scoreBand, gated on round completion.
  'recruiterVerdict',
  'reasonChip',
  'redactedResumeText',
  // US-492 (#112) added both fields to CalibrationCandidateRow. They must be
  // listed here or `contractKeys` fails the build with MISSING_FROM_CONTRACT_KEYS.
  'recruiterNote',
  'judgedAt',
  // US-503 (#122) added researchSeconds to the row. contractKeys<T>() is
  // exhaustive, so omitting it is a compile error naming the missing key —
  // which is how this was found, rather than as a blank field in the UI.
  'researchSeconds',
]);

export const CALIBRATION_JOB_CONTEXT_KEYS = contractKeys<CalibrationJobContext>()([
  'id',
  'title',
  'location',
  'requirements',
  'requirementsTruncated',
  'screeningCriteriaText',
]);

export const CALIBRATION_ROUND_INFO_KEYS = contractKeys<CalibrationRoundInfo>()([
  'id',
  'roundNumber',
  'status',
  'batchSize',
  'candidateCount',
  'failureReason',
]);

export const CALIBRATION_CANDIDATES_RESPONSE_KEYS = contractKeys<CalibrationCandidatesResponse>()([
  'candidates',
  'job',
  'round',
]);

export const CALIBRATION_IDENTITY_RESPONSE_KEYS = contractKeys<CalibrationIdentityResponse>()([
  'candidateId',
  'candidateName',
  'email',
  'phone',
  'srCandidateId',
  'srProfileUrl',
  'revealedAt',
]);

export const REFINEMENT_DIFF_ENTRY_KEYS = contractKeys<RefinementDiffEntry>()(['field', 'action', 'term']);

export const REFINEMENT_PROPOSAL_ROW_KEYS = contractKeys<RefinementProposalRow>()([
  'id',
  'status',
  'proposedDiff',
  'reasoning',
]);

export const REFINEMENT_PROPOSAL_RESPONSE_KEYS = contractKeys<RefinementProposalResponse>()([
  'proposal',
  'appliedDiff',
  'droppedDiffEntries',
  'droppedReason',
]);
